import gql from 'graphql-tag';
import { store } from 'Models/Store';
import { IStudyPlanEntityAttributes, StudyPlanEntity } from 'Models/Entities';
import { getAttributes } from 'Util/EntityUtils';
import { Conditions, Has, OrderBy } from 'Util/QueryHelpers';

export const GET_STUDY_PLANS_WITH_NAME = gql`
	query getStudyPlansForName($name: String) {
		studyPlanEntitys(where: [{ path: "name", comparison: contains, value: [$name] }]) {
			id
			name
		}
	}`;

export const GET_STUDY_LOAD_CONFIG_FOR_PLAN = gql`
	query getStudyLoadConfigForStudyPlan($args: [WhereExpressionGraph]) {
		studyPlanEntity(where: $args) {
			programVersion {
				id
				studyPeriodConfigs {
					id
					minimum
					maximum
					studyPeriod {
						id
						name
						order
					}
				}
				termConfigs {
					id
					minimum
					maximum
					increment
					defaultLoad
					term {
						id
						name
						order
						studyPeriod {
							id
							order
						}
					}
				}
			}
		}
	}`;

export const GET_STUDY_PLANS_WITH_OWNER_NAME = gql`
	query getStudyPlans(
		$conditions: [[WhereExpressionGraph]],
		$has: [[HasConditionType]],
		$skip: Int,
		$take: Int,
		$orderBy: [OrderByGraph],
		$ids: [ID],
		$id: ID
	) {
		studyPlanEntitys(
			conditions: $conditions
			has: $has
			skip: $skip
			take: $take
			orderBy: $orderBy
			ids: $ids
			id: $id
		) {
			programVersion {
				id
				versionDisplayName
				status
				program {
					id
					code
					displayName
				}
			}
			# These properties are custom resolvers that need to be manually specified
			ownerName
			ownerUserType
			modifiedByUserName # this property a custom resolver that needs to be manually specified
			modifiedByUserType
			${getAttributes(StudyPlanEntity).join('\n')}
		}
		countStudyPlanEntitys(
			conditions: $conditions
			has: $has
			ids: $ids
			id: $id
		) {
			number
		}
	}`;

export const getStudyPlansByName = async (planName: string) => {
	const { data } = await store.apolloClient
		.query<{
			studyPlanEntitys: IStudyPlanEntityAttributes[];
		}>({
			query: GET_STUDY_PLANS_WITH_NAME,
			fetchPolicy: 'network-only',
			variables: {
				name: planName,
			},
		});

	return data;
};

export const getStudyLoadConfig = async (studyPlanId: string) => {
	const { data } = await store.apolloClient
		.query<{ studyPlanEntity: StudyPlanEntity }>(
			{
				query: GET_STUDY_LOAD_CONFIG_FOR_PLAN,
				fetchPolicy: 'network-only',
				variables: {
					args: [
						{ path: 'id', comparison: 'equal', value: studyPlanId },
					],
				},
			},
		);

	return data;
};

export const getStudyPlansFilters = (userId: string) => {
	return store.apolloClient
		.query<{
			studyPlanEntitys: IStudyPlanEntityAttributes[],
		}>({
			query: gql`
				query getStudyPlans(
					$conditions: [[WhereExpressionGraph]],
				) {
					studyPlanEntitys(
						conditions: $conditions
					) {
						programVersion {
							id
							versionDisplayName
							commencingYear
							status
							program {
								id
								code
								title
							}
						}
						owner
						ownerName
						modifiedBy
						modifiedByUserName
					}
				}`,
			variables: {
				conditions: [[
					{
						path: 'studentId',
						comparison: 'equal',
						value: [userId],
					},
					{
						path: 'studentAdvisorId',
						comparison: 'equal',
						value: [userId],
					},
					{
						path: 'institutionAdminId',
						comparison: 'equal',
						value: [userId],
					},
					{
						path: 'curriculumSupportId',
						comparison: 'equal',
						value: [userId],
					},
					{
						path: 'adminId',
						comparison: 'equal',
						value: [userId],
					},
				]],
			},
			fetchPolicy: 'network-only',
		});
};

export const getStudyPlans = (
	conditions: Conditions,
	has: Has[][],
	orderBy: OrderBy,
	skip: number,
	take: number,
	abortSignal: AbortSignal,
) => {
	return store.apolloClient
		.query<{
			studyPlanEntitys: IStudyPlanEntityAttributes[],
			countStudyPlanEntitys: { number: number },
		}>({
			query: GET_STUDY_PLANS_WITH_OWNER_NAME,
			variables: {
				conditions,
				has,
				orderBy,
				skip,
				take,
			},
			fetchPolicy: 'network-only',
			context: {
				fetchOptions: {
					signal: abortSignal,
				},
			},
		});
};

export const getStudyPlansByUserId = async (
	userId: string,
	conditions: Conditions,
	has: Has[][],
	orderBy: OrderBy,
	skip: number,
	take: number,
	abortSignal: AbortSignal,
) => {
	const userConditions: Conditions = [
		[
			{
				path: 'studentId',
				comparison: 'equal',
				value: [userId],
			},
			{
				path: 'studentAdvisorId',
				comparison: 'equal',
				value: [userId],
			},
			{
				path: 'institutionAdminId',
				comparison: 'equal',
				value: [userId],
			},
			{
				path: 'curriculumSupportId',
				comparison: 'equal',
				value: [userId],
			},
			{
				path: 'adminId',
				comparison: 'equal',
				value: [userId],
			},
		],
	];

	return getStudyPlans(
		[...userConditions, ...conditions],
		has,
		orderBy,
		skip,
		take,
		abortSignal,
	);
};
